import type {
  AttributionRequestBody,
  EventSource,
  ProjectAttributionRequestItem,
  ProjectDay,
  ProjectPeriodLock,
} from "../../../api/schemas";
import { formatInstant, parseInstant, toLocalDate } from "../../../domain/time";
import type { EpochSeconds, LocalDate } from "../../../domain/time";

/**
 * Maximum length of the reason of a request and of a decision note
 * (`ProjectAttributionRequest.MaxTextLength`).
 */
export const maxRequestReasonLength = 500;

/** Problem with an entered reason. */
export type ReasonProblem = "required" | "tooLong";

/**
 * Returns whether the projects of a past day can be changed in "Meine Zeiten" (F-07, spec 6.5.4):
 * project time tracking is available (tenant switch and entitlement `Projects`), the requirement on
 * that day is not `Disabled`, and the day is closed with working time. The requirement mode
 * (`Optional` included) and the age of the day do not matter; locked periods are handled per
 * change.
 *
 * @param isAvailable - Whether project time tracking is available (`GET /me/project-days`).
 * @param day - The project view of the day, if the server sent one.
 * @returns Whether the day editor may be offered.
 */
export function canAttributeDay(isAvailable: boolean, day: ProjectDay | undefined): boolean {
  const attributable =
    isAvailable &&
    day !== undefined &&
    day.requirement !== "Disabled" &&
    day.isClosed &&
    day.workSeconds > 0;

  return attributable;
}

/**
 * Returns whether the day view shows its project line (F-61, F-24): only when the person may choose
 * a project on the day or time of the day is attributed to a project, or the person asked for an
 * attribution of the day. A server before F-61 does not tell the choice; the line stays then.
 *
 * @param day - The project view of the day.
 * @param requests - Number of the person's own attribution requests about the day.
 * @returns `true` if the line is shown.
 */
export function showsProjectLine(day: ProjectDay, requests: number): boolean {
  const shows = day.hasSelectableProjects !== false || day.projects.length > 0 || requests > 0;

  return shows;
}

/**
 * Returns the locks that are in force on a day (not released, the day within their period).
 *
 * @param locks - Locked project periods.
 * @param date - The local date.
 * @returns The locks covering the day.
 */
export function activeLocksOn(
  locks: readonly ProjectPeriodLock[],
  date: LocalDate,
): ProjectPeriodLock[] {
  // ISO dates compare correctly as strings.
  const active = locks.filter(
    (item) => item.releasedAt === null && item.from <= date && date <= item.to,
  );

  return active;
}

/**
 * Returns whether time attributed to a project is locked on a day: a lock of every project, or a
 * lock of exactly this project (`ProjectPeriodLock.Covers` in the backend).
 *
 * @param dayLocks - Locks covering the day ({@link activeLocksOn}).
 * @param projectId - The project, or `null` for unattributed time.
 * @returns Whether the time is locked.
 */
export function isProjectLocked(
  dayLocks: readonly ProjectPeriodLock[],
  projectId: string | null,
): boolean {
  const locked = dayLocks.some(
    (item) => item.projectId === null || (projectId !== null && item.projectId === projectId),
  );

  return locked;
}

/**
 * Returns whether changing a part of a day from one project to another needs a request (spec 6.5.6
 * `PINV-P05`): the server refuses an attribution whose old or new project is locked on that day.
 * The server stays authoritative; this only avoids a rejection that the person would have to
 * resolve later.
 *
 * @param dayLocks - Locks covering the day ({@link activeLocksOn}).
 * @param before - The current project of the part.
 * @param after - The wanted project.
 * @returns Whether the change is locked.
 */
export function isChangeLocked(
  dayLocks: readonly ProjectPeriodLock[],
  before: string | null,
  after: string | null,
): boolean {
  const locked =
    before !== after && (isProjectLocked(dayLocks, before) || isProjectLocked(dayLocks, after));

  return locked;
}

/**
 * Returns the instant a request starts at (switch instant or range start).
 *
 * @param request - The request.
 * @returns The start, or `null` if the request has neither.
 */
export function requestStart(request: ProjectAttributionRequestItem): EpochSeconds | null {
  const start = request.occurredAt ?? request.rangeStart;
  const instant = start === null ? null : parseInstant(start);

  return instant;
}

/**
 * Returns the requests of an employee whose time lies on a day, oldest first.
 *
 * @param requests - Listed requests.
 * @param employeeId - The employee.
 * @param date - The local date.
 * @param timeZone - Time zone of the day.
 * @returns The requests of that day.
 */
export function ownRequestsOnDay(
  requests: readonly ProjectAttributionRequestItem[],
  employeeId: string | null,
  date: LocalDate,
  timeZone: string,
): ProjectAttributionRequestItem[] {
  const onDay = requests
    .filter((request) => {
      const start = requestStart(request);
      const matches =
        request.employeeId === employeeId &&
        start !== null &&
        toLocalDate(start, timeZone) === date;

      return matches;
    })
    .sort((left, right) => left.requestedAt.localeCompare(right.requestedAt));

  return onDay;
}

/**
 * Returns the requests the current user may decide now, oldest first. The server sets `mayDecide`
 * (pending, not about or by the current user, HR or project responsibility), so the four-eyes rule
 * is not repeated here.
 *
 * @param requests - Listed requests.
 * @returns The requests to decide.
 */
export function requestsToDecide(
  requests: readonly ProjectAttributionRequestItem[],
): ProjectAttributionRequestItem[] {
  const toDecide = requests
    .filter((request) => request.status === "Pending" && request.mayDecide)
    .sort((left, right) => left.requestedAt.localeCompare(right.requestedAt));

  return toDecide;
}

/**
 * Validates the reason of a request.
 *
 * @param reason - The entered reason.
 * @returns The problem, or `null` if the reason is fine.
 */
export function validateReason(reason: string): ReasonProblem | null {
  const trimmed = reason.trim();
  const problem: ReasonProblem | null =
    trimmed === "" ? "required" : trimmed.length > maxRequestReasonLength ? "tooLong" : null;

  return problem;
}

/** Input of {@link rangeAttributionBody}. */
export interface RangeAttributionInput {
  /** Client-generated UUIDv7; the attribution keeps it on approval. */
  readonly id: string;
  readonly projectId: string | null;
  readonly start: EpochSeconds;
  /** End of the range (exclusive). */
  readonly end: EpochSeconds;
  readonly source: EventSource;
}

/**
 * Builds the attribution of a request for a part of a day: a range, which wins over switches
 * (spec 6.5.3), recorded online because requests need a connection.
 *
 * @param input - The values.
 * @returns The attribution of `POST /project-attribution-requests`.
 */
export function rangeAttributionBody(input: RangeAttributionInput): AttributionRequestBody {
  const body: AttributionRequestBody = {
    id: input.id,
    employeeId: null,
    kind: "Range",
    projectId: input.projectId,
    occurredAt: null,
    rangeStart: formatInstant(input.start),
    rangeEnd: formatInstant(input.end),
    supersedesAttributionId: null,
    note: null,
    source: input.source,
    recordedOffline: false,
  };

  return body;
}

/**
 * Returns the label of a project ("code · name") from a list of known projects.
 *
 * @param projects - Known projects.
 * @param projectId - The project.
 * @returns The label, or `null` if the project is not in the list.
 */
export function projectLabelOf(
  projects: readonly { readonly id: string; readonly code: string; readonly name: string }[],
  projectId: string,
): string | null {
  const project = projects.find((item) => item.id === projectId);
  const label = project === undefined ? null : `${project.code} · ${project.name}`;

  return label;
}

/** One project of a day with its working time. */
export interface ProjectShare {
  /** The project, or `null` for the unattributed time. */
  readonly projectId: string | null;
  readonly seconds: number;
}

/**
 * Lists the projects of a day with their working time, followed by the unattributed time.
 *
 * @param day - The project view of the day.
 * @returns The shares in the order of the server, unattributed time last.
 */
export function projectShares(day: ProjectDay): ProjectShare[] {
  const shares: ProjectShare[] = day.projects.map((item) => ({
    projectId: item.projectId,
    seconds: item.seconds,
  }));
  const withOpen =
    day.unattributedWorkSeconds > 0
      ? [...shares, { projectId: null, seconds: day.unattributedWorkSeconds }]
      : shares;

  return withOpen;
}
